// SPDX-License-Identifier: Elastic-2.0
/**
 * locationMock — fixture + MSW handler factory for A3 Location (T-016).
 *
 * F-300 `GET /location/detect` is not yet implemented (Lane A is in
 * flight). Until it lands the dev server returns 404, so this module
 * ships two complementary pieces:
 *
 *  1. `BUENOS_AIRES_LOCATION_FIXTURE` — a canonical Buenos Aires payload
 *     used by every consumer (the hook's fallback, the test handlers,
 *     and the manual-override modal as defaults).
 *  2. `buildLocationDetectHandler()` — an MSW handler factory that
 *     returns the fixture (or an arbitrary override). Test files import
 *     this and wire it up with `setupServer(...)` from `msw/node`. We
 *     don't boot MSW globally — per the orchestrator's per-test policy
 *     (mirrors T-015's pattern) — so importing this file from production
 *     code is safe even though it references `msw`.
 *
 * Why a fixture in a `.ts` module (rather than a `.json` file): the
 * downstream consumer needs a typed object (`LocationDetectResponse`),
 * not raw JSON. Co-locating both shape and value keeps the contract in
 * one place.
 *
 * When Lane A ships `GET /location/detect`, this module survives:
 *  - tests keep using the handler factory to stub responses;
 *  - the dev fallback in `useLocationDetect` switches from "use this
 *    fixture on 404" to "use the real response", but the fixture stays
 *    as the safety net for offline / first-boot.
 */
import { http, HttpResponse } from "msw";

/**
 * Shape of the F-300 `GET /location/detect` response.
 *
 * `country` is not in the functional-spec §3.5 sketch but is necessary
 * for the City card's secondary line and is included in the mock so the
 * UI has the same data shape regardless of where the value originates.
 */
export interface LocationDetectResponse {
  lat: number;
  lng: number;
  /** IANA timezone identifier. e.g. "America/Argentina/Buenos_Aires". */
  tz: string;
  city: string;
  country: string;
}

/**
 * Default fixture used by `useLocationDetect` and the manual-override
 * modal. Buenos Aires picked because it's the project's primary dev
 * locale and the tz has no DST — keeps offset computation deterministic.
 */
export const BUENOS_AIRES_LOCATION_FIXTURE: LocationDetectResponse = {
  lat: -34.6037,
  lng: -58.3816,
  tz: "America/Argentina/Buenos_Aires",
  city: "Buenos Aires",
  country: "Argentina",
};

/**
 * Build an MSW handler for `GET /location/detect`.
 *
 * Defaults to the Buenos Aires fixture; tests can pass any partial
 * override to exercise other shapes (different tz, edge-case names,
 * etc.). The handler matches the path pattern at any host so tests
 * don't need to know the gateway base URL.
 *
 * Usage in a test:
 * ```ts
 * import { setupServer } from "msw/node";
 * import { buildLocationDetectHandler } from "./mocks/locationMock";
 *
 * const server = setupServer(buildLocationDetectHandler());
 * beforeAll(() => server.listen());
 * afterAll(() => server.close());
 * ```
 */
export function buildLocationDetectHandler(
  overrides: Partial<LocationDetectResponse> = {},
) {
  const body: LocationDetectResponse = { ...BUENOS_AIRES_LOCATION_FIXTURE, ...overrides };
  return http.get("*/location/detect", () => HttpResponse.json(body));
}
